import {useMemoizedValue} from '@sail/react';
import {useContext, useMemo, useState} from 'react';
import {DEFAULT_DEBUG_BOUNDARY_VISIBLE} from 'src/internal/constants';
import DebugBoundaryVisibleContext from 'src/internal/provider/DebugBoundaryVisibleContext';
import ObservabilityApiProvider from 'src/internal/provider/ObservabilityApiProvider';
import ObservabilityConfigProvider from 'src/internal/provider/ObservabilityConfigProvider';
import DebugBoundary from 'src/public/DebugBoundary';

import type {ReactElement, ReactNode} from 'react';
import type {ObservabilityConfig} from 'src/types';

type Props = {
  children?: ReactNode;
  updateMainHubOnMount?: boolean;
} & ObservabilityConfig;

/**
 * @see https://trailhead.corp.stripe.com/docs/sail-observability/api/ObservabilityProvider
 * @see https://trailhead.corp.stripe.com/docs/sail-observability/api/ErrorBoundary
 */
export default function ObservabilityProvider({
  children,
  updateMainHubOnMount,
  ...config
}: Props): ReactElement {
  const stableConfig = useMemoizedValue(config);
  const debugBoundaryVisibleParent = useContext(DebugBoundaryVisibleContext);

  const [visibleCategories, setVisibleCategories] = useState<Set<string>>(
    new Set(),
  );

  const debugBoundaryVisibleCurrent = useMemo(
    () => [visibleCategories, setVisibleCategories] as const,
    [visibleCategories],
  );

  const debugBoundaryVisible =
    debugBoundaryVisibleParent === DEFAULT_DEBUG_BOUNDARY_VISIBLE
      ? debugBoundaryVisibleCurrent
      : debugBoundaryVisibleParent;

  return (
    <DebugBoundaryVisibleContext.Provider value={debugBoundaryVisible}>
      <ObservabilityConfigProvider config={stableConfig}>
        <ObservabilityApiProvider updateMainHubOnMount={updateMainHubOnMount}>
          <DebugBoundary
            category="ObservabilityProvider"
            name={stableConfig.project}
            color="#513DD9"
          >
            {children}
          </DebugBoundary>
        </ObservabilityApiProvider>
      </ObservabilityConfigProvider>
    </DebugBoundaryVisibleContext.Provider>
  );
}
